import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import PercentIcon from "@mui/icons-material/Percent";
import type { StrategyListItemV2 } from "../../types/strategy";
import { traderTheme as theme } from "./traderDashboardTheme";
import { getExchangeLogoByName } from "../../utils/exchangeUrls";
import {
  formatConfiguredFundingRate,
  getFundingRateColor,
} from "../../utils/futuresFormatters";

export interface UpdateEntryFundingRateDialogProps {
  open: boolean;
  strategy: StrategyListItemV2 | null;
  onClose: () => void;
  onConfirm: (
    entryFundingRateThreshold: number | null,
    entryFundingIntervalHours?: number,
  ) => void;
  loading: boolean;
}

const ENTRY_FUNDING_INTERVAL_OPTIONS = [0, 1, 4, 8] as const;

export const UpdateEntryFundingRateDialog: React.FC<
  UpdateEntryFundingRateDialogProps
> = ({ open, strategy, onClose, onConfirm, loading }) => {
  const { t } = useTranslation();
  const [entryFundingRate, setEntryFundingRate] = useState("");
  const [entryFundingIntervalHours, setEntryFundingIntervalHours] =
    useState<string>("8");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !strategy) return;
    const currentValue =
      strategy.hedge_config?.rebalancing?.entry_funding_rate_threshold;
    const currentInterval =
      strategy.hedge_config?.rebalancing?.entry_funding_interval_hours ?? 8;
    setEntryFundingRate(currentValue == null ? "" : String(currentValue * 100));
    setEntryFundingIntervalHours(String(currentInterval));
    setError("");
  }, [open, strategy]);

  const handleConfirm = () => {
    const hasEntryFundingRate = entryFundingRate.trim() !== "";
    const nextValue =
      entryFundingRate.trim() === ""
        ? null
        : parseFloat(entryFundingRate) / 100;
    const nextInterval = hasEntryFundingRate
      ? parseInt(entryFundingIntervalHours, 10)
      : undefined;

    if (
      entryFundingRate.trim() !== "" &&
      (nextValue === null || Number.isNaN(nextValue))
    ) {
      setError(t("traderDashboard.updateEntryFundingRate.errorInvalidValue"));
      return;
    }

    if (
      hasEntryFundingRate &&
      (nextInterval === undefined ||
        Number.isNaN(nextInterval) ||
        !ENTRY_FUNDING_INTERVAL_OPTIONS.some((hours) => hours === nextInterval))
    ) {
      setError(
        t("traderDashboard.updateEntryFundingRate.errorInvalidInterval"),
      );
      return;
    }

    const lowFundingRate =
      strategy?.hedge_config?.rebalancing?.low_funding_rate_threshold;
    if (
      nextValue !== null &&
      lowFundingRate !== undefined &&
      lowFundingRate !== null &&
      nextValue < lowFundingRate
    ) {
      setError(
        t("traderDashboard.updateEntryFundingRate.errorBelowLowFundingRate"),
      );
      return;
    }

    setError("");
    onConfirm(nextValue, nextInterval);
  };

  const symbol = strategy?.symbol ?? "-";
  const exchangeName = strategy?.spot_exchange_name ?? "";
  const currentThreshold =
    strategy?.hedge_config?.rebalancing?.entry_funding_rate_threshold;
  const currentInterval =
    strategy?.hedge_config?.rebalancing?.entry_funding_interval_hours ?? 8;
  const lowFundingRate =
    strategy?.hedge_config?.rebalancing?.low_funding_rate_threshold;
  const currentFundingRate = strategy?.funding_rate;
  const currentFundingRateDisplay =
    currentFundingRate == null
      ? t("common.notAvailable")
      : `${(currentFundingRate * 100).toFixed(5)}%`;
  const hasEntryFundingRate = entryFundingRate.trim() !== "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.bg.primary,
          border: `1px solid ${theme.border}`,
          color: theme.text.primary,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accent,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {strategy && (
            <Avatar
              src={getExchangeLogoByName(exchangeName)}
              sx={{ width: 28, height: 28 }}
            />
          )}
          <PercentIcon sx={{ color: theme.accent }} />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: theme.accent }}
          >
            {t("traderDashboard.updateEntryFundingRate.title", { symbol })}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography
          variant="body2"
          sx={{ color: theme.text.secondary, mb: 2.5 }}
        >
          {t("traderDashboard.updateEntryFundingRate.description")}
        </Typography>

        {strategy && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.bg.hover,
              borderRadius: 1,
              mb: 3,
              border: `1px solid ${theme.border}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: theme.text.secondary, mb: 1 }}
            >
              {t("traderDashboard.updateEntryFundingRate.currentConfig")}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.text.secondary }}>
              {t("traderDashboard.updateEntryFundingRate.currentFundingRate")}:{" "}
              <Typography
                component="span"
                sx={{
                  color:
                    currentFundingRate == null
                      ? theme.text.primary
                      : getFundingRateColor(currentFundingRate),
                  fontWeight: 600,
                }}
              >
                {currentFundingRateDisplay}
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.text.secondary, mt: 0.5 }}
            >
              {t("traderDashboard.updateEntryFundingRate.currentThreshold")}:{" "}
              <Typography
                component="span"
                sx={{ color: theme.text.primary, fontWeight: 600 }}
              >
                {currentThreshold == null
                  ? t("traderDashboard.updateEntryFundingRate.notSet")
                  : formatConfiguredFundingRate(currentThreshold)}
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.text.secondary, mt: 0.5 }}
            >
              {t("traderDashboard.updateEntryFundingRate.currentInterval")}:{" "}
              <Typography
                component="span"
                sx={{ color: theme.text.primary, fontWeight: 600 }}
              >
                {currentThreshold == null
                  ? t("traderDashboard.updateEntryFundingRate.intervalDisabled")
                  : currentInterval === 0
                    ? t(
                        "traderDashboard.updateEntryFundingRate.intervalDontCare",
                      )
                    : t(
                        "traderDashboard.updateEntryFundingRate.intervalHoursValue",
                        { count: currentInterval },
                      )}
              </Typography>
            </Typography>
            {lowFundingRate != null && (
              <Typography
                variant="body2"
                sx={{ color: theme.text.secondary, mt: 0.5 }}
              >
                {t("traderDashboard.updateEntryFundingRate.lowFundingRate")}:{" "}
                <Typography
                  component="span"
                  sx={{ color: theme.text.primary, fontWeight: 600 }}
                >
                  {(lowFundingRate * 100).toFixed(2)}%
                </Typography>
              </Typography>
            )}
          </Box>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            fullWidth
            type="number"
            label={t("traderDashboard.updateEntryFundingRate.fieldLabel")}
            value={entryFundingRate}
            onChange={(e) => setEntryFundingRate(e.target.value)}
            placeholder={t(
              "traderDashboard.updateEntryFundingRate.placeholder",
            )}
            helperText={t("traderDashboard.updateEntryFundingRate.helperText")}
            InputProps={{
              inputProps: { step: 0.001 },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          />

          <TextField
            select
            fullWidth
            label={t("traderDashboard.updateEntryFundingRate.intervalLabel")}
            value={entryFundingIntervalHours}
            onChange={(e) => setEntryFundingIntervalHours(e.target.value)}
            helperText={
              hasEntryFundingRate
                ? t("traderDashboard.updateEntryFundingRate.intervalHelperText")
                : t(
                    "traderDashboard.updateEntryFundingRate.intervalDisabledHelperText",
                  )
            }
            disabled={loading || !strategy || !hasEntryFundingRate}
            SelectProps={{ native: true }}
            sx={{
              flex: { xs: 1, sm: "0 0 160px" },
              "& .MuiOutlinedInput-root": {
                color: theme.text.primary,
                "& fieldset": { borderColor: theme.border },
                "&:hover fieldset": { borderColor: theme.accent },
              },
              "& .MuiInputLabel-root": { color: theme.text.secondary },
              "& .MuiFormHelperText-root": { color: theme.text.secondary },
            }}
          >
            {ENTRY_FUNDING_INTERVAL_OPTIONS.map((hours) => (
              <option key={hours} value={hours}>
                {hours === 0
                  ? t("traderDashboard.updateEntryFundingRate.intervalDontCare")
                  : t(
                      "traderDashboard.updateEntryFundingRate.intervalHoursValue",
                      { count: hours },
                    )}
              </option>
            ))}
          </TextField>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ color: theme.text.secondary }}
        >
          {t("traderDashboard.clearPosition.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !strategy}
          startIcon={loading ? <CircularProgress size={16} /> : <PercentIcon />}
          sx={{
            backgroundColor: theme.accent,
            color: theme.bg.primary,
            "&:hover": {
              backgroundColor: theme.accentHover,
            },
          }}
        >
          {loading
            ? t("traderDashboard.updateEntryFundingRate.updating")
            : t("traderDashboard.updateEntryFundingRate.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateEntryFundingRateDialog;
